import { Button, Col, Collapse, Radio, RadioGroupProps, Row, Space } from 'antd';
import { useRouter } from 'next/router';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import PopoverTicketDetail from '../../../../../components/PopoverTicketDetail';
import useAssociableTickets from '../../../../../hooks/ticketDetailPage/useAssociableTickets';
import useCurrentTicket from '../../../../../hooks/ticketDetailPage/useCurrentTicket';
import { Comment, DetailedUser } from '../../../../../model/types';
import { MergeTicketPostData, cancelMerge, getTicketById, mergeTicket } from '../../../../../services/tickets/tickets';
import { useTabStore } from '../../../../../store/RootStoreContext';
import { by, getMaskedEmailForUI } from '../../../../../utils/helpers';
import requestWithMessage from '../../../../../utils/requestWithMessage';
import { filterSWRKeys } from '../../../../../utils/swr';
import { getTicketKey } from '../../../../../utils/ticketHelper';

const RadioGroup = Radio.Group;

const style = {
    radioStyle: {
        display: 'flex',
        height: '30px',
        lineHeight: '30px'
    },
    ticketInfoColStyle: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '300px'
    } as CSSProperties
};

const { Panel } = Collapse;

const emptyTicketKeyValue = '0';

export const getLastTwoComments = (comments: Comment[]): Comment[] => {
    const sortedComments = comments?.sort(by<Comment>('createdAt')('descending'));

    return sortedComments.length > 1 ? [sortedComments[0], sortedComments[1]] : sortedComments;
};

const CallTicketAssociatonContainer = ({ ticketRequester }: { ticketRequester: DetailedUser }): JSX.Element => {
    const {
        push,
        query: { ticketKey }
    } = useRouter();
    const { t } = useTranslation('tickets');
    const { mutate } = useCurrentTicket();
    const { mutate: globalMutate } = useSWRConfig();

    const requesterId = ticketRequester?.id;
    const { data: assignableTicketList } = useAssociableTickets(requesterId);

    const { closeTabAndGetNewTargetUrl } = useTabStore();

    const [ticketKeyToAssociate, setTicketKeyToAssociate] = useState(emptyTicketKeyValue);

    const [loading, setLoading] = useState(false);

    const onChange: RadioGroupProps['onChange'] = ({ target: { value } }) => {
        setTicketKeyToAssociate(value);
    };

    const buttonText =
        ticketKeyToAssociate === emptyTicketKeyValue
            ? t('associateButtonText.dont')
            : t('associateButtonText.associate', {
                  ticketKey: getTicketKey(assignableTicketList?.find(({ key }) => key === ticketKeyToAssociate))
              });

    const handleOnClick = async () => {
        setLoading(true);

        let startMessage: string;
        let finishMessage: string;

        if (ticketKeyToAssociate === emptyTicketKeyValue) {
            startMessage = t('message.onDontAssociateStart', { ticketKey });
            finishMessage = t('message.onDontAssociateFinish', { ticketKey });
            await requestWithMessage({
                key: 'associate-ticket',
                startMessage,
                finishMessage,
                apiRequest: async () => {
                    await cancelMerge(ticketKey as string);

                    await mutate();
                },
                errorConfig: {
                    onApiRequestError: (_error) => {
                        setLoading(false);
                    }
                }
            });
            return;
        }

        startMessage = t('message.onAssociateStart', { ticketKey, ticketKeyToAssociate });
        finishMessage = t('message.onAssociateFinish', { ticketKey, ticketKeyToAssociate });
        await requestWithMessage({
            key: 'associate-ticket',
            startMessage,
            finishMessage,
            apiRequest: async () => {
                const mergeTicketPostData: MergeTicketPostData = {
                    sourceKey: ticketKey as string,
                    targetKey: ticketKeyToAssociate,
                    resolution: 'MERGED_BY_CALL'
                };
                await mergeTicket(mergeTicketPostData);

                closeTabAndGetNewTargetUrl(ticketKey?.toString());
                await globalMutate(
                    filterSWRKeys(`/tickets/${ticketKeyToAssociate}?ui=true`),
                    getTicketById(ticketKeyToAssociate)
                );
                await push(`/tickets/${ticketKeyToAssociate}`);
            },
            errorConfig: {
                onApiRequestError: (_error) => {
                    setLoading(false);
                }
            }
        });
        setLoading(false);
    };

    if (!assignableTicketList || assignableTicketList?.length === 0) {
        return null;
    }

    return (
        <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
            <Collapse defaultActiveKey="1">
                <Panel header={t('associate.description', { ticketKey })} key="1">
                    <Space
                        direction="vertical"
                        className="full-width"
                        size="middle"
                        style={{ marginBottom: '1rem' }}
                        id="CallTicketAssociatonContainer"
                    >
                        <RadioGroup onChange={onChange} value={ticketKeyToAssociate} className="full-width">
                            <Radio key="dont-associate" value={emptyTicketKeyValue} style={style.radioStyle}>
                                {t('dontAssociateText')}
                            </Radio>

                            {assignableTicketList?.map((assignableTicket) => (
                                <Radio key={assignableTicket.key} value={assignableTicket.key} style={style.radioStyle}>
                                    <Row className="full-width" wrap={false}>
                                        <Col
                                            style={style.ticketInfoColStyle}
                                        >{`${assignableTicket.key}: ${assignableTicket.subject}`}</Col>
                                        <Col span={8} offset={1}>
                                            <PopoverTicketDetail
                                                ticketKey={assignableTicket.key}
                                                subject={assignableTicket.subject}
                                                ticketStatus={assignableTicket.status.name}
                                                assignee={
                                                    assignableTicket.assignee?.user?.email
                                                        ? getMaskedEmailForUI(assignableTicket.assignee?.user?.email)
                                                        : assignableTicket.assignee?.group?.name ?? '-'
                                                }
                                                lastTwoComments={getLastTwoComments(assignableTicket.comments)}
                                                placement="right"
                                                style={{ minWidth: 400 }}
                                            >
                                                <a> {t('seeDetails')}</a>
                                            </PopoverTicketDetail>
                                        </Col>
                                    </Row>
                                </Radio>
                            ))}
                        </RadioGroup>
                        <Button type="primary" onClick={handleOnClick} loading={loading}>
                            {buttonText}
                        </Button>
                    </Space>
                </Panel>
            </Collapse>
        </div>
    );
};

export default CallTicketAssociatonContainer;
